import { AR_CATALOGUE, translate, type Locale } from '@qms360/shared';

/**
 * The interface language (spec §4, §15; ADR-0014; remediation pass 12).
 *
 * `t(english, vars)` returns the text in the signed-in person's language. The
 * English is the key; the Arabic catalogue lives in the shared package, so the
 * API can say its messages in Arabic too. The locale is set from the session;
 * the app remounts when it changes (see `App` in main.tsx), so no component has
 * to subscribe to it.
 *
 * Numbers and dates go through `formatDate`/`formatDateTime`/`formatNumber`,
 * which follow the same locale: Arabic keeps the Gregorian calendar and Latin
 * digits, as quality records are kept (OPEN_QUESTIONS Q30).
 */

let current: Locale = 'en';

export function setUiLocale(locale: Locale): void {
  current = locale;
}

export function uiLocale(): Locale {
  return current;
}

export function t(text: string | null | undefined, vars?: Record<string, string | number | null | undefined>): string {
  // A label looked up in a map may be missing; it renders as nothing, as before.
  if (text === null || text === undefined) return '';
  return translate(current === 'ar' ? AR_CATALOGUE : null, text, vars);
}

/** The Intl locale for the interface language. */
export function intlLocale(): string {
  return current === 'ar' ? 'ar-SA-u-ca-gregory-nu-latn' : 'en-GB';
}

type DateInput = string | number | Date;

export function formatDate(value: DateInput, options?: Intl.DateTimeFormatOptions): string {
  return new Date(value).toLocaleDateString(intlLocale(), options);
}

export function formatDateTime(value: DateInput, options?: Intl.DateTimeFormatOptions): string {
  return new Date(value).toLocaleString(intlLocale(), options);
}

export function formatTime(value: DateInput, options?: Intl.DateTimeFormatOptions): string {
  return new Date(value).toLocaleTimeString(intlLocale(), options);
}

export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return value.toLocaleString(intlLocale(), options);
}

/** A master-data row's name in the interface language, when it has one (`nameAr`, `labelAr`). */
export function localName(row: { name?: string | null; nameAr?: string | null; label?: string | null; labelAr?: string | null }): string {
  if (current === 'ar') {
    const arabic = row.nameAr ?? row.labelAr;
    if (arabic) return arabic;
  }
  return row.name ?? row.label ?? '';
}
